import type { Chat } from '@ai-sdk/react';
import type { CrowdexAgentInteraction, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { ApiClientError } from '@crowdex/host-kit/renderer/http';
import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import type { ChatRequestOptions } from 'ai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ConversationConnection, useAgentRuntime } from '@/hooks/use-agent-runtime';
import { useStopGeneration } from '@/hooks/use-conversation-runtime';
import { useCrowdexChat } from '@/hooks/use-crowdex-chat';
import { createCrowdexChat } from '@/lib/agent-runtime/chat';
import { requestMessagePrediction } from '@/lib/agent-runtime/message-prediction';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { AnswerToolApproval } from '@/lib/conversations/message-part/tool-approval';
import { resolveAgentForRuntime } from '@/lib/desktop-shell/cloud-agents';
import { conversationRunPlace } from '@/lib/desktop-shell/conversation-run-place';
import { createConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import type { DesktopShellStore, InitialTranscript } from '@/lib/desktop-shell/store';
import { useConversationChanges } from '@/lib/desktop-shell/use-conversation-changes';
import { isLocalMachine } from '@/lib/local-runtime';
import {
  endNativeBrowserTurn,
  type NativeAgent,
  readNativeWorkspaceEnvironment,
} from '@/lib/native-desktop';
import {
  latestReplyModelId,
  latestSessionAgentName,
  latestSessionConfigOptions,
} from '@/lib/session-events';

type AgentConnection = ReturnType<typeof useAgentRuntime>;

interface UseDesktopConversationChatOptions {
  store: DesktopShellStore;
  connection: AgentConnection;
  chatId: string;
  /** Seeds this conversation's chat only when it names it. */
  initialTranscript: InitialTranscript;
  cloudAgentSelected: boolean;
  selectedCloudRuntime: CloudRuntime | null;
  initialThreadHydrationReady: boolean;
  agentRuntime: 'cloud' | 'local';
  agentId: string | null;
  agents: readonly NativeAgent[];
}

/**
 * Owns the chat of each conversation opened in this window and the connection its transport is
 * (`ConversationConnection`), replaced together: the shown conversation's follows the shell's
 * agent and folder, another's follows its own place when it sends in the background. A turn's
 * approvals, interactions and stop go through its conversation's connection, read when they are
 * asked.
 */
export function useDesktopConversationChat({
  store,
  connection,
  chatId,
  initialTranscript,
  cloudAgentSelected,
  selectedCloudRuntime,
  initialThreadHydrationReady,
  agentRuntime,
  agentId,
  agents,
}: UseDesktopConversationChatOptions) {
  const chatStoreRef = useRef(new Map<string, Chat<CrowdexUIMessage>>());
  const chatConnectionStoreRef = useRef(new Map<string, ConversationConnection>());
  const conversations = useMemo(
    () => createConversationScopes(store, chatStoreRef.current),
    [store],
  );

  /** The connection a conversation's chat sends through now. */
  const connectionOf = useCallback(
    (id: string): ConversationConnection | undefined => chatConnectionStoreRef.current.get(id),
    [],
  );

  /**
   * The conversation's chat on `next`: the one it has while its transport is that connection's,
   * or while a turn streams through it (replacing a live Chat detaches the UI from its reader
   * while the server keeps producing); else a new one with its transcript.
   */
  const chatOn = useCallback(
    (
      id: string,
      next: ConversationConnection,
      seed: () => readonly CrowdexUIMessage[],
    ): Chat<CrowdexUIMessage> => {
      const current = chatStoreRef.current.get(id);
      if (
        current !== undefined &&
        (chatConnectionStoreRef.current.get(id) === next ||
          current.status === 'submitted' ||
          current.status === 'streaming')
      )
        return current;
      const messages =
        current !== undefined && current.messages.length > 0 ? [...current.messages] : [...seed()];
      chatConnectionStoreRef.current.set(id, next);
      const chat = createConversationChat(store, id, messages, next.transport, connectionOf, () =>
        chatStoreRef.current.get(id),
      );
      chatStoreRef.current.set(id, chat);
      return chat;
    },
    [connectionOf, store],
  );

  const activeChat = chatOn(chatId, connection.conversationConnection, () =>
    initialTranscript.chatId === chatId ? initialTranscript.messages : [],
  );

  const chat = useCrowdexChat(
    activeChat,
    cloudAgentSelected ? selectedCloudRuntime : null,
    cloudAgentSelected || isLocalMachine(),
    initialThreadHydrationReady,
  );
  // Through the connection its turn runs on now, whichever render asks.
  const respondToInteraction = useCallback(
    async (interaction: CrowdexAgentInteraction) => {
      const current = connectionOf(chatId);
      if (current === undefined)
        throw new Error('The active agent interaction is no longer available.');
      await current.respondToInteraction(interaction);
    },
    [chatId, connectionOf],
  );
  const answerToolApproval: AnswerToolApproval = async ({ remember, ...input }) => {
    await respondToInteraction({
      type: 'approval',
      id: input.id,
      approved: input.approved,
      ...(remember === true ? { remember } : {}),
    });
    await chat.addToolApprovalResponse(input);
  };

  useConversationChanges(store, chatStoreRef);

  useEffect(() => {
    const configOptions = latestSessionConfigOptions(chat.messages);
    if (configOptions !== null) connection.setReportedConfigOptions(configOptions);
    connection.setReplyModelId(latestReplyModelId(chat.messages));
  }, [connection.setReportedConfigOptions, connection.setReplyModelId, chat.messages]);

  useEffect(() => {
    if (!initialThreadHydrationReady) return;
    const recordedName = latestSessionAgentName(chat.messages);
    const recorded =
      recordedName === null
        ? undefined
        : agents.find((candidate) => candidate.name === recordedName);
    const target =
      recorded === undefined
        ? undefined
        : (resolveAgentForRuntime(agents, recorded.command, agentRuntime) ?? recorded);
    if (target !== undefined && target.id !== agentId) store.getState().setAgentId(target.id);
  }, [agentId, agentRuntime, agents, chat.messages, initialThreadHydrationReady, store]);

  // Bound to this render's conversation: a change made after an await still lands in it, where
  // the SDK's own setter writes to whichever chat is on screen by then.
  const setMessages = useCallback(
    (update: Parameters<typeof chat.setMessages>[0]) =>
      conversations.of(chatId).setMessages(update),
    [chatId, conversations],
  );

  /**
   * Sends a message in a conversation that is not shown, through its own chat on its own
   * connection, where it runs now: its turn runs on the server, and the sidebar shows it running.
   * `take` hands the message over once that connection is there (null: it no longer waits). Says
   * whether it could: not while that conversation has no agent or folder here yet.
   */
  const { connectionFor } = connection;
  const sendInBackground = useCallback(
    async (
      id: string,
      take: () => { message: PromptInputMessage; options?: ChatRequestOptions } | null,
    ): Promise<boolean> => {
      const place = await conversationRunPlace(
        store.getState(),
        id,
        agents,
        readNativeWorkspaceEnvironment,
      );
      const next = place === null ? null : connectionFor(id, place);
      if (next === null) return false;
      const request = take();
      if (request === null) return true;
      const background = chatOn(id, next, () => conversations.of(id).messages());
      store.getState().setLoadingThreadIds((current) => new Set(current).add(id));
      if (request.options === undefined) void background.sendMessage(request.message);
      else void background.sendMessage(request.message, request.options);
      return true;
    },
    [agents, chatOn, connectionFor, conversations, store],
  );

  return {
    ...chat,
    setMessages,
    answerToolApproval,
    respondToInteraction,
    chatStoreRef,
    conversations,
    sendInBackground,
    stopAgentResponse: useStopGeneration(
      chat.stop,
      async () => await connectionOf(chatId)?.cancelGeneration(),
      () => endNativeBrowserTurn(chatId),
    ),
  };
}

/**
 * A conversation's chat. Everything it asks of the agent (client tools, their results, the
 * prediction after a reply) goes through the connection its conversation sends through when it
 * is asked, never one captured when the chat was made.
 */
function createConversationChat(
  store: DesktopShellStore,
  chatId: string,
  messages: CrowdexUIMessage[],
  transport: ConversationConnection['transport'],
  connectionOf: (id: string) => ConversationConnection | undefined,
  currentChat: () => Chat<CrowdexUIMessage> | undefined,
): Chat<CrowdexUIMessage> {
  const connection = () => {
    const current = connectionOf(chatId);
    if (current === undefined) throw new Error('The conversation is not connected.');
    return current;
  };
  return createCrowdexChat({
    id: chatId,
    messages,
    transport,
    executeClientTool: async (name, input) => await connection().executeClientTool(name, input),
    submitClientToolResult: async (result) =>
      await connection().respondToInteraction({ type: 'tool-result', ...result }),
    onFinish: ({ message, messages: finishedMessages, isAbort, isDisconnect, isError }) => {
      void endNativeBrowserTurn(chatId, !isAbort && !isDisconnect && !isError).catch(
        () => undefined,
      );
      const current = store.getState();
      if (
        !isAbort &&
        !isDisconnect &&
        !isError &&
        message.role === 'assistant' &&
        current.appSettings.messagePredictions
      ) {
        requestMessagePrediction(chatId, message.id, (signal) =>
          connection().predictNextMessage(signal),
        );
      }
      current.setConversationCache((cache) => {
        const conversation = cache[chatId];
        if (conversation === undefined) return cache;
        return {
          ...cache,
          [chatId]: {
            ...conversation,
            messages: [...finishedMessages],
            updatedAt: Date.now(),
          },
        };
      });
      current.setLoadingThreadIds((threadIds) => {
        if (!threadIds.has(chatId)) return threadIds;
        const next = new Set(threadIds);
        next.delete(chatId);
        return next;
      });
    },
    onError: (error) => {
      void endNativeBrowserTurn(chatId).catch(() => undefined);
      returnRefusedMessage(store, chatId, currentChat(), error);
    },
  });
}

/**
 * A message the server refused before recording anything of its turn (no agent can answer, its
 * folder is gone) goes back to the composer instead of staying in a transcript that will not keep
 * it: fixing what is missing and sending again loses nothing.
 */
function returnRefusedMessage(
  store: DesktopShellStore,
  chatId: string,
  chat: Chat<CrowdexUIMessage> | undefined,
  error: Error,
): void {
  if (!(error instanceof ApiClientError)) return;
  if (error.reason !== 'no_agent' && error.reason !== 'folder_missing') return;
  const last = chat?.messages.at(-1);
  if (chat === undefined || last?.role !== 'user') return;
  chat.messages = chat.messages.slice(0, -1);
  const text = last.parts.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('\n');
  if (text.length === 0) return;
  store
    .getState()
    .setPaneDrafts((current) =>
      (current[chatId] ?? '').length > 0 ? current : { ...current, [chatId]: text },
    );
}
